/**
 * API Client - Fetch wrapper tipado para chamadas REST
 * Cliente HTTP para API REST
 */

import type { BullProposalResponse } from "@/lib/bull-api-types";
import { autoLogoutOn401 } from "@/lib/auth-utils";
import type { ContractTermsAcceptanceInput } from "@/lib/proposals/contract-terms";

const BASE_URL = "/api/v1";

/**
 * Sanitiza mensagens de erro removendo referências a sistemas internos
 * que não devem ser expostos ao usuário final
 */
function sanitizeErrorMessage(message: string): string {
  return message
    // Remove variações de "Bull" (case insensitive)
    .replace(/\bBull\s*(API)?\b/gi, "sistema")
    .replace(/\bbull\s*(api)?\b/gi, "sistema")
    // Remove paths internos que podem conter "bull"
    .replace(/\/bull[^/\s]*/gi, "")
    // Limpa espaços duplicados
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Validates that an ID is a valid non-empty string
 * Prevents API calls with undefined/null/empty IDs
 */
function validateId(id: string, context: string): void {
  if (!id || id === "undefined" || id === "null") {
    console.error(`[API Client] Invalid ID detected in ${context}:`, id);
    throw new Error(`Invalid ID provided to ${context}: ${id}`);
  }
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Opções extras para fetchApi
 */
interface FetchApiOptions extends RequestInit {
  timeout?: number; // Timeout em ms (default: sem timeout)
}

/**
 * Fetch wrapper com tratamento de erros e tipagem
 */
async function fetchApi<T>(
  endpoint: string,
  options?: FetchApiOptions
): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;

  // Para métodos que normalmente têm body (POST, PUT, PATCH),
  // enviar body vazio "{}" se não foi especificado
  // Isso evita erro de parsing JSON quando Content-Type é application/json mas body está vazio
  const method = options?.method?.toUpperCase();
  const needsBody = method === "POST" || method === "PUT" || method === "PATCH";
  const hasBody = options?.body !== undefined;

  // Configurar AbortController para timeout
  const controller = new AbortController();
  const timeoutId = options?.timeout
    ? setTimeout(() => controller.abort(), options.timeout)
    : null;

  try {
    const res = await fetch(url, {
      ...options,
      ...(needsBody && !hasBody && { body: "{}" }),
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
      credentials: "include", // Incluir cookies para autenticação
      signal: controller.signal,
    });

  // Parse response body
  let data: unknown;
  const contentType = res.headers.get("content-type");
  
  if (contentType?.includes("application/json")) {
    data = await res.json();
  } else {
    data = await res.text();
  }

  if (!res.ok) {
    // 401 = Token expirado ou inválido -> Logout automático
    if (res.status === 401) {
      console.warn("[API Client] 401 Unauthorized - Executando logout automático");
      autoLogoutOn401();
    }

    // Extrai mensagem de erro - suporta estruturas aninhadas da API
    // Formato 1: { message: "string" }
    // Formato 2: { message: { message: "string", error: "...", statusCode: ... } }
    const errorData = data as {
      message?: string | { message?: string; error?: string; statusCode?: number };
      code?: string;
      details?: unknown;
      error?: string;
    };

    let errorMessage: string;
    if (typeof errorData.message === "object" && errorData.message?.message) {
      // Mensagem aninhada (ex: resposta da Bull API)
      errorMessage = errorData.message.message;
    } else if (typeof errorData.message === "string") {
      errorMessage = errorData.message;
    } else if (errorData.error) {
      errorMessage = errorData.error;
    } else {
      errorMessage = `HTTP ${res.status}: ${res.statusText}`;
    }

    throw new ApiError(
      sanitizeErrorMessage(errorMessage),
      res.status,
      errorData.code,
      errorData.details ?? errorData.message // Preserva detalhes completos
    );
  }

  return data as T;
  } catch (error) {
    // Tratar erro de timeout (AbortError)
    if (error instanceof Error && error.name === "AbortError") {
      throw new ApiError(
        "A requisição excedeu o tempo limite. Por favor, tente novamente.",
        408, // Request Timeout
        "TIMEOUT"
      );
    }
    throw error;
  } finally {
    // Limpar timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

function appendSearchParams(
  query: URLSearchParams,
  params?: Record<string, string | number | boolean | null | undefined>,
) {
  if (!params) {
    return;
  }

  for (const [key, value] of Object.entries(params)) {
    // Ignorar valores undefined, null, e strings vazias
    if (value !== undefined && value !== null && value !== "") {
      // Para números, garantir que não seja NaN
      if (typeof value === "number" && Number.isNaN(value)) {
        continue;
      }
      query.set(key, String(value));
    }
  }
}

/**
 * API client tipado
 * Organizado por domínio (users, proposals, etc.)
 */
export const api = {
  users: {
    list: (params?: { search?: string; companyId?: string; squadId?: string }) => {
      const query = new URLSearchParams();
      if (params?.search) query.set("search", params.search);
      if (params?.companyId) query.set("companyId", params.companyId);
      if (params?.squadId) query.set("squadId", params.squadId);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/users${queryString ? `?${queryString}` : ""}`);
    },
    listAll: (params?: { companyId?: string; squadId?: string }) => {
      const query = new URLSearchParams();
      if (params?.companyId) query.set("companyId", params.companyId);
      if (params?.squadId) query.set("squadId", params.squadId);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/users/all${queryString ? `?${queryString}` : ""}`);
    },
    getById: (id: string) => fetchApi<{ data: unknown }>(`/users/${id}`),
    create: (data: unknown) => fetchApi<{ data: unknown }>("/users", {
      method: "POST",
      body: JSON.stringify(data),
    }),
    update: (id: string, data: unknown) => fetchApi<{ data: unknown }>(`/users/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    }),
    delete: (id: string) => fetchApi<{ success: boolean }>(`/users/${id}`, {
      method: "DELETE",
    }),
    canDelete: (id: string) => fetchApi<{ data: { canDelete: boolean; entityType: string; entityId: string; entityName: string | null; dependencies: Array<{ type: string; count: number; message: string; blocking: boolean }> } }>(`/users/${id}/can-delete`),
    permanentDelete: (id: string) => fetchApi<{ success: boolean }>(`/users/${id}/permanent`, {
      method: "DELETE",
    }),
    listWithoutSquad: (params?: { companyId?: string; search?: string }) => {
      const query = new URLSearchParams();
      if (params?.companyId) query.set("companyId", params.companyId);
      if (params?.search) query.set("search", params.search);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/users/without-squad${queryString ? `?${queryString}` : ""}`);
    },
    listWithSquads: () => fetchApi<{ data: Array<{ userId: string; squadId: string }> }>("/users/with-squads"),
    setPassword: (data: { token: string; password: string }) => 
      fetchApi<{ success: boolean; message: string }>("/users/set-password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    getProfile: (id: string) => fetchApi<{ data: { id: string; userId: string; squadId: string | null; profile: 'partner_master' | 'team_master' | 'digitador'; financial: boolean } | null }>(`/users/${id}/profile`),
    createOrUpdateProfile: (id: string, data: { profile: 'partner_master' | 'team_master' | 'digitador'; squadId: string | null; financial: boolean }) =>
      fetchApi<{ data: unknown }>(`/users/${id}/profile`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  
  proposals: {
    list: (params?: {
      page?: number;
      limit?: number;
      status?: string;
      statuses?: string[];
      companyId?: string;
      squadId?: string;
      typedBy?: string;
      startDate?: string;
      endDate?: string;
      search?: string;
      product?: string;
      proposalNumber?: string;
      clientName?: string;
      typedByName?: string;
      companyName?: string;
      liberatedValueMin?: number;
      liberatedValueMax?: number;
      createdAtStart?: string;
      createdAtEnd?: string;
      typedAtStart?: string;
      typedAtEnd?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }) => {
      const query = new URLSearchParams();
      if (params) {
        const { statuses, ...rest } = params;
        appendSearchParams(query, rest);
      }
      if (params?.statuses?.length) {
        query.set("statuses", params.statuses.join(","));
      }
      const queryString = query.toString();
      return fetchApi<{ data: unknown[]; pagination: unknown }>(`/proposals${queryString ? `?${queryString}` : ""}`);
    },
    getById: async (id: string): Promise<{ data: unknown }> => {
      return fetchApi<{ data: unknown }>(`/proposals/${id}`);
    },
    /**
     * Get complete proposal details from Bull API
     * Returns selected_offer, contract_details, digital_signature, etc.
     * Used for partner API integration
     */
    getDetails: async (id: string): Promise<{ data: BullProposalResponse }> => {
      return fetchApi<{ data: BullProposalResponse }>(`/partner/proposals/${id}/details`);
    },
    getRejectionReason: (id: string) => fetchApi<{ data: { rejection_reason: string | null } | null }>(`/proposals/${id}/rejection-reason`),
    create: (data: unknown) => fetchApi<{ data: unknown }>("/proposals/start", {
      method: "POST",
      body: JSON.stringify(data),
    }),
    /**
     * Start Simulation - Orchestrated endpoint
     * Creates proposal + sends contact in ONE request
     * Returns proposalId + tokenExpiresAt + step
     */
    startSimulation: (data: {
      cpf: string;
      full_name: string;
      birth_date: string;
      city: string;
      state: string;
      phone: string;
      email: string;
    }) => fetchApi<{ data: { proposalId: string; bullProposalId: string; step: number; status: string; tokenExpiresAt: string; message: string } }>("/proposals/start-simulation", {
      method: "POST",
      body: JSON.stringify(data),
    }),
    update: (id: string, data: unknown) => fetchApi<{ data: unknown }>(`/proposals/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
    cancel: (id: string, data: { reasonTopic: string; reasonDetails: string }) => fetchApi<{ success: boolean }>(`/proposals/${id}`, {
      method: "DELETE",
      body: JSON.stringify({
        reason_topic: data.reasonTopic,
        reason_details: data.reasonDetails,
      }),
    }),
    sendContact: (id: string, data: unknown) => 
      fetchApi<{ data: unknown }>(`/proposals/${id}/contact`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    simulate: (id: string, data: unknown) => 
      fetchApi<{ data: unknown }>(`/proposals/${id}/simulate`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    submitBankingData: (id: string, data: unknown) => 
      fetchApi<{ data: { success: boolean; step: number; status: string; formalizationLink?: string } }>(`/proposals/${id}/banking-data`, {
        method: "POST",
        body: JSON.stringify(data),
        timeout: 60000, // 60 segundos para geração de contrato síncrona
      }),
    // ❌ DEPRECATED: Use updateStep(5) instead
    /**
     * @deprecated Use updateStep(5) instead. Backend orchestrates contract generation automatically.
     */
    generateContract: (id: string, data: unknown) => 
      fetchApi<{ data: unknown }>(`/proposals/${id}/contract`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    validateToken: (id: string, data: { token: string }) =>
      fetchApi<{ data: { success: boolean; step: number; status: string; offers?: unknown[]; rejectionReason?: string; analysisStatusMessage?: string; statusDetailDescription?: string } }>(`/proposals/${id}/validate-and-analyze`, {
        method: "POST",
        body: JSON.stringify(data),
        timeout: 60000, // 60 segundos - limite da Vercel
      }),
    resendToken: (id: string) => 
      fetchApi<{ success: boolean; message?: string }>(`/proposals/${id}/resend-token`, {
        method: "POST",
      }),
    dashboard: (params?: { startDate?: string; endDate?: string; companyId?: string; squadId?: string }) => {
      const query = new URLSearchParams();
      appendSearchParams(query, params);
      const queryString = query.toString();
      return fetchApi<{ data: unknown }>(`/proposals/dashboard${queryString ? `?${queryString}` : ""}`);
    },
    findOpenByCpf: (cpf: string) => fetchApi<{ data: unknown }>("/proposals/find-open-by-cpf", {
      method: "POST",
      body: JSON.stringify({ cpf }),
    }),
    
    // ✅ NOVO: Selecionar oferta (faz pre-accept síncrono)
    selectOffer: (id: string, offerId: string, acceptance: ContractTermsAcceptanceInput) =>
      fetchApi<{ data: { success: boolean; step: number; status: string; selectedOfferId: string; contractTermsStatus: "Aceito" } }>(`/proposals/${id}/select-offer`, {
        method: "POST",
        body: JSON.stringify({ offerId, ...acceptance }),
        timeout: 60000, // 60 segundos para pre-accept síncrono
      }),
    
    listBanks: () => fetchApi<{ data: unknown[] }>("/proposals/banks"),
    
    markAsTyped: (id: string, contractUrl?: string) =>
      fetchApi<{ data: unknown }>(`/proposals/${id}/mark-as-typed`, {
        method: "POST",
        body: JSON.stringify({ contractUrl }),
      }),
    sync: (id: string) =>
      fetchApi<{ data: unknown }>(`/proposals/${id}/sync`, {
        method: "POST",
      }),
  },
  
  auth: {
    getMe: () => fetchApi<{ data: unknown }>("/auth/me"),
    updateProfile: (data: unknown) => fetchApi<{ data: unknown }>("/auth/profile", {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
    changePassword: (userId: string, data: { currentPassword: string; newPassword: string }) =>
      fetchApi<{ data: unknown }>(`/users/${userId}/password`, {
        method: "PATCH",
        body: JSON.stringify({ password: data.newPassword, currentPassword: data.currentPassword }),
      }),
    forgotPassword: (data: { email: string }) =>
      fetchApi<{ success?: boolean; message?: string }>("/auth/forgot-password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    resetPassword: (data: { token: string; password: string }) =>
      fetchApi<{ success?: boolean; message?: string }>("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  
  simulations: {
    authorize: (data: { cpf: string }) =>
      fetchApi<{ data: unknown }>("/simulations/authorize", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    simulate: (data: { proposalId: string; installments?: number; loan_amount?: number }) =>
      fetchApi<{ data: unknown }>("/simulations/simulate", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    getResults: (id: string) => fetchApi<{ data: unknown[] }>(`/simulations/${id}/results`),
  },
  
  companies: {
    list: (params?: { search?: string; status?: string; limit?: number; offset?: number }) => {
      const query = new URLSearchParams();
      appendSearchParams(query, params);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[]; pagination: unknown }>(`/companies${queryString ? `?${queryString}` : ""}`);
    },
    create: (data: unknown) => fetchApi<{ data: unknown }>("/companies", {
      method: "POST",
      body: JSON.stringify(data),
    }),
    update: (id: string, data: unknown) => fetchApi<{ data: unknown }>(`/companies/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
    delete: (id: string) => fetchApi<{ data: { success: boolean } }>(`/companies/${id}`, {
      method: "DELETE",
    }),
    canDelete: (id: string) => fetchApi<{ data: { canDelete: boolean; entityType: string; entityId: string; entityName: string | null; dependencies: Array<{ type: string; count: number; message: string; blocking: boolean }> } }>(`/companies/${id}/can-delete`),
    permanentDelete: (id: string) => fetchApi<{ success: boolean }>(`/companies/${id}/permanent`, {
      method: "DELETE",
    }),
  },
  
  teams: {
    list: (params?: { companyId?: string }) => {
      const query = new URLSearchParams();
      if (params?.companyId) query.set("companyId", params.companyId);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/teams${queryString ? `?${queryString}` : ""}`);
    },
    listByCompany: (companyId: string) => {
      return fetchApi<{ data: unknown[] }>(`/teams/list-by-company?companyId=${companyId}`);
    },
    getById: (id: string) => fetchApi<{ data: unknown }>(`/teams/${id}`),
    create: (data: unknown) => fetchApi<{ data: unknown }>("/teams", {
      method: "POST",
      body: JSON.stringify(data),
    }),
    update: (id: string, data: unknown) => fetchApi<{ data: unknown }>(`/teams/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    }),
    delete: (id: string) => fetchApi<{ success: boolean }>(`/teams/${id}`, {
      method: "DELETE",
    }),
    canDelete: (id: string) => fetchApi<{ data: { canDelete: boolean; entityType: string; entityId: string; entityName: string | null; dependencies: Array<{ type: string; count: number; message: string; blocking: boolean }> } }>(`/teams/${id}/can-delete`),
    permanentDelete: (id: string) => fetchApi<{ success: boolean }>(`/teams/${id}/permanent`, {
      method: "DELETE",
    }),
    getMembers: (id: string) => fetchApi<{ data: unknown[] }>(`/teams/${id}/members`),
    addMember: (id: string, data: { userId: string; forceTransfer?: boolean }) =>
      fetchApi<{ data: unknown }>(`/teams/${id}/members`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    removeMember: (id: string, userId: string) =>
      fetchApi<{ success: boolean }>(`/teams/${id}/members?userId=${userId}`, {
        method: "DELETE",
      }),
    transferMember: (id: string, data: { userId: string; fromSquadId?: string; confirmTransfer: boolean }) =>
      fetchApi<{ data: unknown }>(`/teams/${id}/transfer`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
    listAvailableUsers: (id: string, params?: { search?: string; onlyWithoutSquad?: boolean }) => {
      const query = new URLSearchParams();
      if (params?.search) query.set("search", params.search);
      if (params?.onlyWithoutSquad) query.set("onlyWithoutSquad", String(params.onlyWithoutSquad));
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/teams/${id}/available-users${queryString ? `?${queryString}` : ""}`);
    },
  },
  
  clients: {
    list: (params?: {
      page?: number;
      limit?: number;
      search?: string;
      status?: string;
      name?: string;
      cpf?: string;
      createdAtStart?: string;
      createdAtEnd?: string;
      lastProposalPaidAtStart?: string;
      lastProposalPaidAtEnd?: string;
      sortBy?: string;
      sortOrder?: "asc" | "desc";
    }) => {
      const query = new URLSearchParams();
      appendSearchParams(query, params);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[]; pagination?: unknown }>(`/clients${queryString ? `?${queryString}` : ""}`);
    },
    getById: (id: string) => fetchApi<{ data: unknown }>(`/clients/${id}`),
    getByCpf: (cpf: string) => fetchApi<{ data: unknown | null }>(`/clients/cpf/${cpf}`),
    update: (id: string, data: { name?: string; email?: string; phone?: string; birthDate?: string }) =>
      fetchApi<{ data: unknown }>(`/clients/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }),
  },
  
  roles: {
    list: (params?: { companyId?: string }) => {
      const query = new URLSearchParams();
      if (params?.companyId) query.set("companyId", params.companyId);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/roles${queryString ? `?${queryString}` : ""}`);
    },
    listByCompany: (companyId?: string) => {
      const query = new URLSearchParams();
      if (companyId) query.set("companyId", companyId);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/roles${queryString ? `?${queryString}` : ""}`);
    },
    getById: (id: string) => fetchApi<{ data: unknown }>(`/roles/${id}`),
    create: (data: { name: string; description?: string; companyId?: string; isSystem?: boolean; level?: number; permissionIds?: string[] }) =>
      fetchApi<{ data: unknown }>("/roles", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    update: (id: string, data: { name: string; description?: string; permissionIds: string[] }) =>
      fetchApi<{ data: { success: boolean } }>(`/roles/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    delete: (id: string) => fetchApi<{ data: { success: boolean } }>(`/roles/${id}`, {
      method: "DELETE",
    }),
    getPermissions: (roleId: string, companyId?: string) => {
      const query = new URLSearchParams();
      if (companyId) query.set("companyId", companyId);
      const queryString = query.toString();
      return fetchApi<{ data: Array<{ id: string; resource: string; action: string }> }>(`/roles/${roleId}/permissions${queryString ? `?${queryString}` : ""}`);
    },
    assignPermissions: (roleId: string, data: { permissionIds: string[]; companyId?: string }) =>
      fetchApi<{ data: { success: boolean } }>(`/roles/${roleId}/permissions`, {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  
  permissions: {
    list: (params?: { resource?: string; action?: string }) => {
      const query = new URLSearchParams();
      if (params?.resource) query.set("resource", params.resource);
      if (params?.action) query.set("action", params.action);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/permissions${queryString ? `?${queryString}` : ""}`);
    },
    // check removido - usar verificação local baseada em roles ao invés
    getUserPermissions: (params?: { companyId?: string }) => {
      const query = new URLSearchParams();
      if (params?.companyId) query.set("companyId", params.companyId);
      const queryString = query.toString();
      return fetchApi<{ data: Array<{ resource: string; action: string }> }>(`/permissions/user-permissions${queryString ? `?${queryString}` : ""}`);
    },
    canAccessCompany: (companyId: string) => {
      return fetchApi<{ data: { canAccess: boolean } }>(`/permissions/can-access-company?companyId=${companyId}`);
    },
  },
  
  dashboard: {
    getKPIs: (params?: { startDate?: string; endDate?: string; companyId?: string; squadId?: string }) => {
      const query = new URLSearchParams();
      appendSearchParams(query, params);
      const queryString = query.toString();
      return fetchApi<{ data: unknown }>(`/dashboard/kpis${queryString ? `?${queryString}` : ""}`);
    },
    getFunnel: (params?: { startDate?: string; endDate?: string; companyId?: string }) => {
      const query = new URLSearchParams();
      appendSearchParams(query, params);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/dashboard/funnel${queryString ? `?${queryString}` : ""}`);
    },
  },
  
  products: {
    list: () => fetchApi<{ data: unknown[] }>("/products"),
  },
  
  rateTables: {
    list: (params?: { product?: string; companyId?: string }) => {
      const query = new URLSearchParams();
      if (params?.product) query.set("product", params.product);
      if (params?.companyId) query.set("companyId", params.companyId);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/rate-tables${queryString ? `?${queryString}` : ""}`);
    },
    getById: (id: string) => fetchApi<{ data: unknown }>(`/rate-tables/${id}`),
  },
  
  commissions: {
    getSummary: (params?: { startDate?: string; endDate?: string; userId?: string; squadId?: string; companyId?: string }) => {
      const query = new URLSearchParams();
      appendSearchParams(query, params);
      const queryString = query.toString();
      return fetchApi<{ data: unknown }>(`/commissions${queryString ? `?${queryString}` : ""}`);
    },
    getByProposal: (proposalId: string) => fetchApi<{ data: unknown[] }>(`/commissions/proposal/${proposalId}`),
  },
  
  export: {
    proposals: (data: { entityType: 'proposals'; selectedFields: string[]; format: 'csv' | 'xlsx'; reportType?: 'production' | 'commission' | 'topaplus'; filters?: Record<string, unknown> }) => {
      // O backend espera os filtros na raiz do objeto, não aninhados em "filters"
      const { filters, format, selectedFields, reportType } = data;
      const payload = {
        format,
        selectedFields,
        reportType,
        ...filters, // Spread dos filtros para a raiz
      };
      return fetchApi<{ data: unknown }>("/export/proposals", {
        method: "POST",
        body: JSON.stringify(payload),
      });
    },
    getFields: (entityType: 'proposals' | 'users') => {
      const query = new URLSearchParams();
      query.set("entityType", entityType);
      return fetchApi<{ data: unknown[] }>(`/export/fields?${query.toString()}`);
    },
  },
  
  managers: {
    list: (params?: { companyId?: string; search?: string }) => {
      const query = new URLSearchParams();
      if (params?.companyId) query.set("companyId", params.companyId);
      if (params?.search) query.set("search", params.search);
      const queryString = query.toString();
      return fetchApi<{ data: unknown[] }>(`/managers${queryString ? `?${queryString}` : ""}`);
    },
    getById: (id: string) => fetchApi<{ data: unknown }>(`/managers/${id}`),
    create: (data: { name: string; contact: string; companyId?: string }) =>
      fetchApi<{ data: { id: string; name: string; contact: string; companyId?: string } }>("/managers", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },
  
  resources: {
    list: () => fetchApi<{ data: unknown[] }>("/resources"),
    getCategories: () => fetchApi<{ data: string[] }>("/resources/categories"),
    getByName: (name: string) => fetchApi<{ data: unknown }>(`/resources/${name}`),
  },
  
  apiKeys: {
    list: () => fetchApi<{ data: unknown[] }>("/api-keys"),
    create: (data: {
      name: string;
      companyId?: string;
      userId?: string;
      environment?: string;
      allowedIps?: string[];
      allowedEndpoints?: string[];
      rateLimit?: number;
      expiresAt?: string;
    }) =>
      fetchApi<{ data: unknown }>("/api-keys", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    revoke: (id: string) =>
      fetchApi<void>(`/api-keys/${id}`, {
        method: "DELETE",
      }),
  },
};

export default api;
